/**
 * apps/automations-portal/src/features/admin/InboundAddressPanel.tsx
 *
 * The address a client forwards their work to, and the ability to issue one.
 *
 * This is the single most-copied string in onboarding — it goes into an email,
 * then into the client's forwarding rule — so the whole panel is built around
 * making it easy to copy correctly and hard to mistype. A block with no address
 * cannot receive anything, and saying so plainly is more useful than an empty
 * space.
 */
import { useEffect, useState } from 'react';
import { Check, Copy, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, adminApi } from '@/lib/api';
import type { Deployment, InboundAddress } from '@/lib/types';

export function InboundAddressPanel({
  clientId,
  deployments,
}: {
  clientId: string;
  deployments: Deployment[];
}) {
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [addresses, setAddresses] = useState<InboundAddress[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setState('loading');
    adminApi
      .inboundAddresses(clientId)
      .then((data) => {
        setAddresses(data);
        setState('ready');
      })
      .catch(() => setState('error'));
  }

  useEffect(load, [clientId]);

  async function issue(deployment: Deployment) {
    setBusyId(deployment.id);
    setError(null);
    try {
      await adminApi.createInboundAddress(clientId, { deploymentId: deployment.id });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create that address.');
    } finally {
      setBusyId(null);
    }
  }

  async function copy(address: string) {
    await navigator.clipboard.writeText(address);
    setCopied(address);
    window.setTimeout(() => setCopied(null), 2000);
  }

  // Seeded demo rows have no automation behind them, so an address pointing at
  // one would accept mail and do nothing with it.
  const eligible = deployments.filter(
    (d) => !d.seededDemo && !d.missingInEngine && !addresses.some((a) => a.deploymentId === d.id),
  );

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Forwarding addresses
        </h2>
        <p className="text-sm text-muted-foreground">
          What the client forwards their work to. Nothing reaches an automation any other way.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {state === 'loading' && <Skeleton className="h-16 w-full" />}
      {state === 'error' && (
        <p className="text-sm text-destructive">Could not load addresses. Try refreshing.</p>
      )}

      {state === 'ready' && addresses.length === 0 && eligible.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
          Deploy a block first — an address has to point at something.
        </div>
      )}

      {state === 'ready' &&
        addresses.map((a) => (
          <div
            key={a.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3"
          >
            <div className="flex min-w-0 items-center gap-2">
              <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="truncate font-mono text-sm">{a.address}</p>
                <p className="text-xs text-muted-foreground">
                  {a.blockName ?? 'Unknown block'}
                  {!a.active && ' — switched off'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!a.active && <Badge variant="secondary">Off</Badge>}
              <Button type="button" variant="outline" size="sm" onClick={() => void copy(a.address)}>
                {copied === a.address ? (
                  <>
                    <Check className="mr-1 h-3.5 w-3.5" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busyId === a.id}
                onClick={() => {
                  setBusyId(a.id);
                  void adminApi
                    .setInboundAddressActive(clientId, a.id, !a.active)
                    .then(load)
                    .catch(() => setError('Could not change that address.'))
                    .finally(() => setBusyId(null));
                }}
              >
                {a.active ? 'Switch off' : 'Switch on'}
              </Button>
            </div>
          </div>
        ))}

      {state === 'ready' &&
        eligible.map((d) => (
          <div
            key={d.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed p-3"
          >
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{d.blockName}</span> has no address, so
              nothing can reach it.
            </p>
            <Button
              type="button"
              size="sm"
              disabled={busyId === d.id}
              onClick={() => void issue(d)}
            >
              {busyId === d.id ? 'Creating…' : 'Give it an address'}
            </Button>
          </div>
        ))}
    </div>
  );
}
